import { SidebarOptions } from 'core/config/sidebar-options';
import { useAuthState } from 'core/providers/AuthProvider';
import SharedSidebarItem from './sidebar-item';
import { useDriverMessagesState } from 'core/providers/DriverMessagesProvider';
import { AlertsApiService, DeliveriesApiService } from 'core/api';
import { useCollection } from 'core/hooks/useCollection';
import { IAlertDao } from 'core/api/types/alerts.interface';
import { useMemo } from 'react';
import { IDeliveryDao } from 'core/api/types/deliveries.interface';
import { useAwaitingRequests } from 'core/hooks/useAwaitingRequests';
import { orderBy, where } from 'firebase/firestore';
import { DeliveryRouteAssignment } from 'core/constants/delivery-route-assignment';
import dayjs from 'dayjs';

const SharedSidebar = () => {
  const { userRoles } = useAuthState();
  const { unreadMessages } = useDriverMessagesState();

  const { data: allAlerts } = useCollection<IAlertDao>({
    apiService: AlertsApiService,
  });

  const constraints = useMemo(() => {
    return [
      where('assignedRoute', '==', DeliveryRouteAssignment.NONE),
      where('date', '<=', dayjs().format('YYYY-MM-DD')),
      orderBy('date', 'desc'),
      orderBy('address.town_or_city'),
    ];
  }, []);
  const { data: allDeliveries } = useCollection<IDeliveryDao>({
    apiService: DeliveriesApiService,
    constraints,
  });

  // Requests that stopped and are waiting for a person, counted the same way
  // as alerts and unallocated deliveries so it is noticed without anyone
  // opening the screen to look.
  //
  // Queried on status rather than filtered here: this subscription is a page
  // of documents, so counting the waiting ones within it would cap the badge
  // at a page and read zero once enough requests had arrived — which is
  // precisely when it needs to be right.
  const awaitingRequests = useAwaitingRequests();

  const alerts = useMemo(() => {
    return allAlerts?.filter((alert) => !alert.acknowledged) || [];
  }, [allAlerts]);

  const badges: { [key: string]: number | undefined } = {
    alerts: alerts && alerts.length > 0 ? alerts.length : undefined,
    'driver-messages': unreadMessages.length > 0 ? unreadMessages.length : undefined,
    unallocated: allDeliveries && allDeliveries.length > 0 ? allDeliveries.length : undefined,
    'delivery-requests': awaitingRequests.count > 0 ? awaitingRequests.count : undefined,
  };
  return (
    <div className='border-r flex-grow-0 flex-shrink-0 basis-[288px]'>
      {SidebarOptions.filter((item) => item.roles.some((role) => userRoles?.includes(role))).map((item) => (
        <SharedSidebarItem
          key={item.route}
          icon={item.icon}
          label={item.label}
          route={item.route}
          badgeCount={badges[item.route]}
        />
      ))}
    </div>
  );
};

export default SharedSidebar;
